import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import UserCertificationAssignment from '../components/UserCertificationAssignment'
import UserTrainerExtendedFields, { emptyTrainerExtendedFields } from '../components/UserTrainerExtendedFields'
import UserTrainerFields from '../components/UserTrainerFields'
import UserTrainerHourlyRateSection from '../components/UserTrainerHourlyRateSection'
import useUserCreatePage from '../hooks/useUserCreatePage'
import { emptyStawkiGodzinowe, normalizeStawkiGodzinowe } from '@/shared/hourlyRateTypes'

const defaultUser = {
	nazwa: '',
	email: '',
	alternative_name: '',
	password: '',
	tel: '',
	isAdmin: false,
	isTrainer: false,
	isCoordinator: false,
	miasto: '',
	wojewodztwo: '',
	ulica: '',
	kod_pocztowy: '',
	g_placeid: '',
	g_lat: '',
	g_long: '',
	stawka_godzinowa: '',
	stawki_godzinowe: emptyStawkiGodzinowe(),
	...emptyTrainerExtendedFields,
	certificates: [],
}

const normalizeText = value => `${value ?? ''}`.trim()

export default function UserCreatePage() {
	const { t } = useTranslation()
	const { handleCancel, handleSubmit, submitting } = useUserCreatePage()
	const [form, setForm] = useState(defaultUser)
	const [errors, setErrors] = useState({})

	const updateForm = useCallback(nextFields => {
		setForm(prev => ({
			...prev,
			...nextFields,
		}))
		setErrors(prev => {
			const nextErrors = { ...prev }
			Object.keys(nextFields).forEach(field => {
				delete nextErrors[field]
			})
			return nextErrors
		})
	}, [])

	const validate = useCallback(() => {
		const nextErrors = {}

		if (!normalizeText(form.nazwa)) {
			nextErrors.nazwa = t('settingsPage.users.validation.nameRequired')
		}
		if (!normalizeText(form.email)) {
			nextErrors.email = t('settingsPage.users.validation.emailRequired')
		}
		if (normalizeText(form.email) && !normalizeText(form.email).includes('@')) {
			nextErrors.email = t('settingsPage.users.validation.emailInvalid')
		}
		if (!normalizeText(form.alternative_name)) {
			nextErrors.alternative_name = t('settingsPage.users.validation.alternativeNameRequired')
		}
		if (!normalizeText(form.password)) {
			nextErrors.password = t('settingsPage.users.validation.passwordRequired')
		}
		if (normalizeText(form.password) && normalizeText(form.password).length < 3) {
			nextErrors.password = t('settingsPage.users.validation.passwordMin')
		}
		setErrors(nextErrors)
		return Object.keys(nextErrors).length === 0
	}, [form, t])

	const buildPayload = useCallback(() => ({
		nazwa: normalizeText(form.nazwa),
		email: normalizeText(form.email),
		alternative_name: normalizeText(form.alternative_name),
		password: normalizeText(form.password),
		isAdmin: form.isAdmin,
		isTrainer: form.isTrainer,
		isCoordinator: form.isCoordinator,
		...(form.isTrainer ? {
			tel: normalizeText(form.tel),
			miasto: normalizeText(form.miasto),
			wojewodztwo: normalizeText(form.wojewodztwo),
			ulica: normalizeText(form.ulica),
			kod_pocztowy: normalizeText(form.kod_pocztowy),
			g_placeid: normalizeText(form.g_placeid),
			g_lat: form.g_lat === '' ? '' : Number(form.g_lat),
			g_long: form.g_long === '' ? '' : Number(form.g_long),
			stawki_godzinowe: normalizeStawkiGodzinowe(form.stawki_godzinowe),
			stawka_godzinowa: normalizeText(form.stawki_godzinowe?.U ?? form.stawka_godzinowa),
			prawo_jazdy: Array.isArray(form.prawo_jazdy) ? form.prawo_jazdy : [],
			zatrudnienie: normalizeText(form.zatrudnienie),
			RODO: normalizeText(form.RODO),
			KRK: normalizeText(form.KRK),
			RSTPS: normalizeText(form.RSTPS),
			wyksztalcenie: normalizeText(form.wyksztalcenie),
			dowod_osobisty: normalizeText(form.dowod_osobisty),
			PESEL: normalizeText(form.PESEL),
			pelna_teczka: Boolean(form.pelna_teczka),
			notatka: normalizeText(form.notatka),
			certificates: Array.isArray(form.certificates) ? form.certificates : [],
		} : {}),
	}), [form])

	const onSubmit = useCallback(async event => {
		event.preventDefault()
		if (submitting || !validate()) return
		await handleSubmit(buildPayload())
	}, [buildPayload, handleSubmit, submitting, validate])

	const handleTrainerRoleChange = useCallback(event => {
		const isTrainer = event.target.checked
		updateForm(isTrainer
			? { isTrainer }
			: {
				isTrainer,
				tel: '',
				miasto: '',
				wojewodztwo: '',
				ulica: '',
				kod_pocztowy: '',
				g_placeid: '',
				g_lat: '',
				g_long: '',
				stawka_godzinowa: '',
				stawki_godzinowe: emptyStawkiGodzinowe(),
				...emptyTrainerExtendedFields,
				certificates: [],
			}
		)
	}, [updateForm])

	const handleCertificatesChange = useCallback(certificates => {
		updateForm({ certificates })
	}, [updateForm])

	return (
		<div className="max-w-6xl mx-auto px-0 sm:px-6 lg:px-8">
			<div className="bg-white shadow-sm ring-1 ring-gray-900/5 sm:rounded-xl">
				<div className="px-4 sm:px-6 py-6 border-b border-gray-200">
					<h1 className="text-2xl font-bold text-gray-900">{t('settingsPage.users.createDialog.title')}</h1>
				</div>

				<form className="space-y-6 px-4 py-6 sm:px-6" onSubmit={onSubmit}>
					<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
						<label className="flex flex-col gap-1">
							<span className="text-sm font-medium text-gray-700">{t('settingsPage.users.fields.name')} *</span>
							<input
								type="text"
								value={form.nazwa}
								onChange={event => updateForm({ nazwa: event.target.value })}
								placeholder={t('settingsPage.users.createDialog.placeholders.name')}
								className={`block w-full rounded-md bg-white px-3 py-2 text-sm text-gray-900 outline-1 -outline-offset-1 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 ${
									errors.nazwa ? 'outline-red-300' : 'outline-gray-300'
								}`}
							/>
							{errors.nazwa ? <span className="text-sm text-red-600">{errors.nazwa}</span> : null}
						</label>

						<label className="flex flex-col gap-1">
							<span className="text-sm font-medium text-gray-700">{t('settingsPage.users.fields.email')} *</span>
							<input
								type="email"
								value={form.email}
								onChange={event => updateForm({ email: event.target.value })}
								placeholder={t('settingsPage.users.createDialog.placeholders.email')}
								className={`block w-full rounded-md bg-white px-3 py-2 text-sm text-gray-900 outline-1 -outline-offset-1 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 ${
									errors.email ? 'outline-red-300' : 'outline-gray-300'
								}`}
							/>
							{errors.email ? <span className="text-sm text-red-600">{errors.email}</span> : null}
						</label>

						<label className="flex flex-col gap-1">
							<span className="text-sm font-medium text-gray-700">{t('settingsPage.users.fields.alternativeName')} *</span>
							<input
								type="text"
								value={form.alternative_name}
								onChange={event => updateForm({ alternative_name: event.target.value })}
								placeholder={t('settingsPage.users.createDialog.placeholders.alternativeName')}
								className={`block w-full rounded-md bg-white px-3 py-2 text-sm text-gray-900 outline-1 -outline-offset-1 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 ${
									errors.alternative_name ? 'outline-red-300' : 'outline-gray-300'
								}`}
							/>
							{errors.alternative_name ? <span className="text-sm text-red-600">{errors.alternative_name}</span> : null}
						</label>

						<label className="flex flex-col gap-1">
							<span className="text-sm font-medium text-gray-700">{t('settingsPage.users.fields.password')} *</span>
							<input
								type="password"
								value={form.password}
								onChange={event => updateForm({ password: event.target.value })}
								placeholder={t('settingsPage.users.createDialog.placeholders.password')}
								className={`block w-full rounded-md bg-white px-3 py-2 text-sm text-gray-900 outline-1 -outline-offset-1 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 ${
									errors.password ? 'outline-red-300' : 'outline-gray-300'
								}`}
							/>
							{errors.password ? <span className="text-sm text-red-600">{errors.password}</span> : null}
						</label>

						<div className="md:col-span-2">
							<span className="text-sm font-medium text-gray-700">{t('settingsPage.users.fields.roles')}</span>
							<div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-3">
								<label className="flex items-center gap-2 text-sm text-gray-700">
									<input
										type="checkbox"
										checked={form.isAdmin}
										onChange={event => updateForm({ isAdmin: event.target.checked })}
										className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
									/>
									{t('settingsPage.users.roles.admin')}
								</label>
								<label className="flex items-center gap-2 text-sm text-gray-700">
									<input
										type="checkbox"
										checked={form.isTrainer}
										onChange={handleTrainerRoleChange}
										className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
									/>
									{t('settingsPage.users.roles.trainer')}
								</label>
								<label className="flex items-center gap-2 text-sm text-gray-700">
									<input
										type="checkbox"
										checked={form.isCoordinator}
										onChange={event => updateForm({ isCoordinator: event.target.checked })}
										className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
									/>
									{t('settingsPage.users.roles.coordinator')}
								</label>
							</div>
						</div>
					</div>

					{form.isTrainer ? (
						<>
							<UserTrainerFields
								form={form}
								errors={errors}
								description={t('settingsPage.users.trainerModule.editDescription')}
								telPlaceholder={t('settingsPage.users.createDialog.placeholders.tel')}
								requiredFields={false}
								onChange={updateForm}
							/>
							<UserTrainerHourlyRateSection
								form={form}
								onChange={updateForm}
							/>
							<UserTrainerExtendedFields
								form={form}
								onChange={updateForm}
							/>
							<UserCertificationAssignment
								certificates={form.certificates}
								disabled={submitting}
								onCertificatesChange={handleCertificatesChange}
							/>
						</>
					) : null}

					<div className="flex justify-end gap-3 border-t border-gray-200 pt-4">
						<button
							type="button"
							onClick={handleCancel}
							disabled={submitting}
							className="rounded-md bg-gray-500 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-gray-400 disabled:cursor-not-allowed disabled:opacity-50"
						>
							{t('notifications.cancelButton')}
						</button>
						<button
							type="submit"
							disabled={submitting}
							className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-50"
						>
							{submitting ? t('settingsPage.users.actions.creating') : t('settingsPage.users.createDialog.confirm')}
						</button>
					</div>
				</form>
			</div>
		</div>
	)
}
